import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTriggerOnClickOutsideElement } from '@lmt/unicorn-shared/app/helpers/useTriggerOnClickOutsideElement';
import { IconButton } from '../IconButton';
import { OptionList } from '../OptionList';
import { ButtonColor } from './Button';

export type OptionProps = {
  displayValue: string;
  value: string | number;
  color?: ButtonColor;
  onClick?: () => void;
  testId?: string;
};

interface IconDropDownButtonProps {
  iconComponent: React.ReactNode;
  disabled?: boolean;
  options: OptionProps[];
  onOptionClick?: (value: OptionProps) => void;
  optionListClassName?: string;
  className?: string;
  // programmatic scroll needs to be enabled, if DropDownButton is in scrollable container
  // and drop down could be partially displayed outside the scrollable region (e.g. '+' button in CaseTree)
  enableProgrammaticScroll?: boolean;
  testId?: string;
}

export const IconDropDownButton: React.FC<IconDropDownButtonProps> = (
  props,
) => {
  const {
    iconComponent,
    disabled,
    options,
    onOptionClick,
    optionListClassName,
    className,
    testId,
  } = props;

  const [expanded, setExpanded] = useState<boolean>(false);
  const optionListRef = useRef<HTMLDivElement>(null);
  const iconButtonRef = useRef<HTMLAnchorElement>(null);
  const hide = useCallback(() => setExpanded(false), [setExpanded]);
  useTriggerOnClickOutsideElement(optionListRef, hide, expanded);

  const onScrollHandlerRef = useRef<(() => void) | null>(null);
  const onIconClickHandler = useCallback(() => {
    setExpanded((oldExpandedValue) => {
      return !oldExpandedValue;
    });
  }, []);

  useEffect(
    function handleProgrammaticScrollForOptionsList() {
      if (props.enableProgrammaticScroll) {
        const scrollableParent = findScrollableParent(iconButtonRef.current);
        if (scrollableParent) {
          if (expanded && optionListRef.current && iconButtonRef.current) {
            const initialTop =
              iconButtonRef.current.offsetTop +
              iconButtonRef.current.clientHeight;

            // attach onScroll handler
            onScrollHandlerRef.current = () => {
              if (!optionListRef.current) return;

              optionListRef.current!.style.top =
                initialTop - scrollableParent.scrollTop + 'px';
            };
            onScrollHandlerRef.current();

            scrollableParent.addEventListener(
              'scroll',
              onScrollHandlerRef.current,
            );
            return () =>
              scrollableParent.removeEventListener(
                'scroll',
                onScrollHandlerRef.current!,
              );
          }
        }
      }
      return;
    },
    [props.enableProgrammaticScroll, expanded, optionListRef.current],
  );

  return (
    <div>
      <IconButton
        iconComponent={iconComponent}
        disabled={disabled}
        ref={iconButtonRef}
        onClick={onIconClickHandler}
        enableClickPropagation={true}
        testId={testId ?? 'icon-drop-down'}
        className={className}
      />
      {expanded && (
        <OptionList<OptionProps>
          visible={expanded}
          options={options}
          onClick={(option) => {
            setExpanded(false);
            option.onClick?.();
            onOptionClick?.(option);
          }}
          labelField="displayValue"
          elementRef={optionListRef}
          className={optionListClassName}
        />
      )}
    </div>
  );
};

function isElementScrollable(element: HTMLElement): boolean {
  const hasScrollableContent = element.scrollHeight > element.clientHeight;
  return hasScrollableContent;
}
function findScrollableParent(
  current: HTMLElement | null,
): HTMLElement | undefined | null {
  if (!current) return current;
  if (current && isElementScrollable(current)) return current;
  return findScrollableParent(current?.parentElement);
}
